import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database,
  Activity,
  Calendar,
  Filter,
  Loader2,
  ChevronDown,
  ChevronUp,
  Tag,
  PenLine,
  Briefcase,
  Trash2,
  X,
} from 'lucide-react';
import AppNavActions from '../components/AppNavActions';
import FeedbackPane from '../components/monitoring/FeedbackPane';
import type { FeedbackItem, FeedbackStatistics } from '../components/monitoring/FeedbackPane';
import MonitoringClassificationTable from '../components/monitoring/MonitoringClassificationTable';
import type { MonitoringTableRow } from '../components/monitoring/MonitoringClassificationTable';
import { useApiRoot } from '../contexts/ApiRootContext';
import { apiFetch } from '../lib/apiFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DateFilters {
  dateFrom: string;
  dateTo: string;
}

type MonitoringCardItem = MonitoringTableRow;
type HalLink = { href: string };
type MonitoringLinks = Record<string, HalLink>;

interface DeletedRecordItem {
  id: string;
  title?: string | null;
  deletedAt?: string | null;
  deleter?: string | null;
}

interface MonitoringIndexResponse {
  _links?: MonitoringLinks;
  error?: string;
}

interface MonitoringListResponse {
  _embedded?: {
    records?: MonitoringCardItem[];
  };
  error?: string;
}

interface DeletedRecordsListResponse {
  _embedded?: {
    records?: DeletedRecordItem[];
  };
  error?: string;
}

interface FeedbackList {
  items: FeedbackItem[];
  statistics: FeedbackStatistics | null;
}

interface FeedbackListResponse {
  _embedded?: {
    feedback?: FeedbackItem[];
    statistics?: FeedbackStatistics;
  };
  error?: string;
}

const EMPTY_FEEDBACK: FeedbackList = { items: [], statistics: null };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BRAND_BG = 'bg-emerald-800';
const BRAND_TEXT = 'text-emerald-800';

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateFilters(): DateFilters {
  const today = todayIsoDate();
  return { dateFrom: today, dateTo: today };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildMonitoringQuery(filters: DateFilters): string {
  const params = new URLSearchParams({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  });
  return `?${params.toString()}`;
}

async function fetchMonitoringIndex(monitoringHref: string): Promise<MonitoringLinks> {
  const response = await apiFetch(monitoringHref);
  const body = (await response.json().catch(() => ({}))) as MonitoringIndexResponse;
  if (!response.ok) {
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return body._links ?? {};
}

async function fetchMonitoringList(
  href: string,
  filters: DateFilters,
): Promise<MonitoringCardItem[]> {
  const response = await apiFetch(`${href}${buildMonitoringQuery(filters)}`);
  const body = (await response.json().catch(() => ({}))) as MonitoringListResponse;
  if (!response.ok) {
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return body._embedded?.records ?? [];
}

async function fetchDeletedRecordsList(
  href: string,
  filters: DateFilters,
): Promise<DeletedRecordItem[]> {
  const response = await apiFetch(`${href}${buildMonitoringQuery(filters)}`);
  const body = (await response.json().catch(() => ({}))) as DeletedRecordsListResponse;
  if (!response.ok) {
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return body._embedded?.records ?? [];
}

async function fetchFeedbackList(href: string, filters: DateFilters): Promise<FeedbackList> {
  const response = await apiFetch(`${href}${buildMonitoringQuery(filters)}`);
  const body = (await response.json().catch(() => ({}))) as FeedbackListResponse;
  if (!response.ok) {
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return {
    items: body._embedded?.feedback ?? [],
    statistics: body._embedded?.statistics ?? null,
  };
}

function formatDeletedAt(value?: string | null): string {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

// ---------------------------------------------------------------------------
// Layout components
// ---------------------------------------------------------------------------

function Navbar({ navigate }: { navigate: (path: string) => void }) {
  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div
            className="flex items-center space-x-3 group cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div
              className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}
            >
              <Database className="w-5 h-5" />
            </div>
            <div>
              <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
              <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">
                Automated Records Assistant
              </span>
            </div>
          </div>
          <AppNavActions />
        </div>
      </div>
    </nav>
  );
}

function ClassificationPane({
  title,
  paneIcon,
  records,
  emptyMessage,
  showFinalClassification = false,
  showClassificationStatus = true,
  showSystemSelectedStatus = false,
  classificationKind = 'record',
}: {
  title: string;
  paneIcon: React.ReactNode;
  records: MonitoringCardItem[];
  emptyMessage: string;
  showFinalClassification?: boolean;
  showClassificationStatus?: boolean;
  showSystemSelectedStatus?: boolean;
  classificationKind?: 'record' | 'function';
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className={`shrink-0 bg-white/95 px-5 py-4 text-left backdrop-blur-sm transition-colors hover:bg-gray-50/80 ${
          expanded ? 'border-b border-gray-100' : ''
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {paneIcon}
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="rounded-full bg-emerald-800 px-2.5 py-1 font-medium text-white">
              {records.length} records
            </span>
          </div>
        </div>
      </button>

      {expanded &&
        (records.length === 0 ? (
          <div className="p-5">
            <p className="rounded-lg border border-dashed border-gray-200 py-12 text-center text-sm text-gray-400">
              {emptyMessage}
            </p>
          </div>
        ) : (
          <MonitoringClassificationTable
            records={records}
            showFinalClassification={showFinalClassification}
            showClassificationStatus={showClassificationStatus}
            showSystemSelectedStatus={showSystemSelectedStatus}
            classificationKind={classificationKind}
          />
        ))}
    </div>
  );
}

function DeletedRecordsPane({
  records,
}: {
  records: DeletedRecordItem[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className={`shrink-0 bg-white/95 px-5 py-4 text-left backdrop-blur-sm transition-colors hover:bg-gray-50/80 ${
          expanded ? 'border-b border-gray-100' : ''
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Deleted Records</h2>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
          <span className="rounded-full bg-emerald-800 px-2.5 py-1 text-xs font-medium text-white">
            {records.length} records
          </span>
        </div>
      </button>

      {expanded &&
        (records.length === 0 ? (
          <div className="p-5">
            <p className="rounded-lg border border-dashed border-gray-200 py-12 text-center text-sm text-gray-400">
              No deleted records in this date range
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Deleted</th>
                  <th className="px-5 py-3">Deleted by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((record) => (
                  <tr key={record.id} className="bg-white">
                    <td className="px-5 py-3 align-top">
                      <a
                        href={`/records/${record.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 font-medium text-gray-900 hover:text-emerald-800 hover:underline break-words"
                      >
                        {record.title || record.id}
                      </a>
                    </td>
                    <td className="px-5 py-3 align-top whitespace-nowrap text-gray-600">
                      {formatDeletedAt(record.deletedAt)}
                    </td>
                    <td className="px-5 py-3 align-top break-all text-gray-600">
                      {record.deleter || 'Unknown'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function Monitoring() {
  const navigate = useNavigate();
  const { getHref, loading: rootLoading } = useApiRoot();
  const monitoringHref = getHref('monitoring');

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<DateFilters>(() => defaultDateFilters());
  const [appliedFilters, setAppliedFilters] = useState<DateFilters>(() => defaultDateFilters());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monitoringLinks, setMonitoringLinks] = useState<MonitoringLinks | null>(null);
  const [recordItems, setRecordItems] = useState<MonitoringCardItem[]>([]);
  const [overriddenRecordItems, setOverriddenRecordItems] = useState<MonitoringCardItem[]>([]);
  const [functionItems, setFunctionItems] = useState<MonitoringCardItem[]>([]);
  const [overriddenFunctionItems, setOverriddenFunctionItems] = useState<MonitoringCardItem[]>([]);
  const [deletedItems, setDeletedItems] = useState<DeletedRecordItem[]>([]);
  const [feedback, setFeedback] = useState<FeedbackList>(EMPTY_FEEDBACK);

  const fetchMonitoringData = useCallback(
    async (filters: DateFilters, indexHref: string) => {
      setLoading(true);
      setError(null);

      try {
        const links = await fetchMonitoringIndex(indexHref);
        setMonitoringLinks(links);

        const recordLowHref = links.record_low_confidence?.href;
        const recordOverriddenHref = links.record_overridden?.href;
        const functionLowHref = links.function_low_confidence?.href;
        const functionOverriddenHref = links.function_overridden?.href;
        const deletedHref = links.deleted_records?.href;
        const feedbackHref = links.feedback?.href;

        if (
          !recordLowHref &&
          !recordOverriddenHref &&
          !functionLowHref &&
          !functionOverriddenHref &&
          !deletedHref &&
          !feedbackHref
        ) {
          throw new Error('No monitoring lists available for your access level');
        }

        const [
          recordLow,
          recordOverridden,
          functionLow,
          functionOverridden,
          deletedRecords,
          feedbackList,
        ] = await Promise.all([
          recordLowHref
            ? fetchMonitoringList(recordLowHref, filters)
            : Promise.resolve([]),
          recordOverriddenHref
            ? fetchMonitoringList(recordOverriddenHref, filters)
            : Promise.resolve([]),
          functionLowHref
            ? fetchMonitoringList(functionLowHref, filters)
            : Promise.resolve([]),
          functionOverriddenHref
            ? fetchMonitoringList(functionOverriddenHref, filters)
            : Promise.resolve([]),
          deletedHref
            ? fetchDeletedRecordsList(deletedHref, filters)
            : Promise.resolve([]),
          feedbackHref
            ? fetchFeedbackList(feedbackHref, filters)
            : Promise.resolve(EMPTY_FEEDBACK),
        ]);

        setRecordItems(recordLow);
        setOverriddenRecordItems(recordOverridden);
        setFunctionItems(functionLow);
        setOverriddenFunctionItems(functionOverridden);
        setDeletedItems(deletedRecords);
        setFeedback(feedbackList);
      } catch (err) {
        console.error('Failed to fetch monitoring data:', err);
        const message =
          err instanceof Error ? err.message : 'Failed to load monitoring data';
        setError(
          message.includes('403') || message.toLowerCase().includes('authorisation')
            ? 'Failed to load monitoring data — sign in or check access'
            : `Failed to load monitoring data${message ? `: ${message}` : ''}`,
        );
        setMonitoringLinks(null);
        setRecordItems([]);
        setOverriddenRecordItems([]);
        setFunctionItems([]);
        setOverriddenFunctionItems([]);
        setDeletedItems([]);
        setFeedback(EMPTY_FEEDBACK);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const feedbackHref = monitoringLinks?.feedback?.href;
  const refreshFeedback = useCallback(async () => {
    if (!feedbackHref) return;
    setFeedback(await fetchFeedbackList(feedbackHref, appliedFilters));
  }, [appliedFilters, feedbackHref]);

  useEffect(() => {
    if (rootLoading) return;
    if (!monitoringHref) {
      setError('Monitoring is not available');
      setMonitoringLinks(null);
      setRecordItems([]);
      setOverriddenRecordItems([]);
      setFunctionItems([]);
      setOverriddenFunctionItems([]);
      setDeletedItems([]);
      setFeedback(EMPTY_FEEDBACK);
      return;
    }
    fetchMonitoringData(appliedFilters, monitoringHref);
  }, [appliedFilters, fetchMonitoringData, monitoringHref, rootLoading]);

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setFilterOpen(false);
  };

  const clearFilters = () => {
    const defaults = defaultDateFilters();
    setDraftFilters(defaults);
    setAppliedFilters(defaults);
  };

  const showLoading = rootLoading || loading;

  return (
    <div className="flex-1 bg-gray-50/50 text-gray-900 font-sans">
      <Navbar navigate={navigate} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-emerald-600" />
              Monitoring
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Review classifications, deleted records and user feedback
            </p>
          </div>
          <div className="relative ml-auto flex shrink-0 flex-col items-end gap-3">
            <button
              onClick={() => setFilterOpen((o) => !o)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all ${
                filterOpen
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                  : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Filter by Date
              {filterOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {filterOpen && (
              <div className="absolute right-0 top-full z-20 mt-2 w-[min(100vw-2rem,320px)] rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-emerald-600" />
                    Date range
                  </h2>
                  <button
                    onClick={clearFilters}
                    className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                  >
                    <X className="w-3.5 h-3.5" /> Reset to today
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Date from</span>
                    <input
                      type="date"
                      value={draftFilters.dateFrom}
                      onChange={(e) =>
                        setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))
                      }
                      className="mt-1 block w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Date to</span>
                    <input
                      type="date"
                      value={draftFilters.dateTo}
                      onChange={(e) =>
                        setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))
                      }
                      className="mt-1 block w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setDraftFilters({ ...appliedFilters });
                      setFilterOpen(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {showLoading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading monitoring data…
          </div>
        ) : error ? (
          <div className="text-center py-24 text-red-500">{error}</div>
        ) : (
          <div className="flex flex-col gap-8">
            {(monitoringLinks?.record_low_confidence ||
              monitoringLinks?.record_overridden) && (
              <section className="space-y-4">
                <div className="flex flex-col gap-6">
                  {monitoringLinks.record_low_confidence && (
                    <ClassificationPane
                      title="Record Classification — Low Confidence"
                      paneIcon={<Tag className="w-5 h-5 text-emerald-600" />}
                      records={recordItems}
                      showFinalClassification
                      showClassificationStatus={false}
                      emptyMessage="No low-confidence record classifications in this date range"
                    />
                  )}
                  {monitoringLinks.record_overridden && (
                    <ClassificationPane
                      title="Record Classification — Overridden"
                      paneIcon={<PenLine className="w-5 h-5 text-violet-600" />}
                      records={overriddenRecordItems}
                      showFinalClassification
                      emptyMessage="No manually overridden record classifications in this date range"
                    />
                  )}
                </div>
              </section>
            )}

            {(monitoringLinks?.function_low_confidence ||
              monitoringLinks?.function_overridden) && (
              <section className="space-y-4">
                <div className="flex flex-col gap-6">
                  {monitoringLinks.function_low_confidence && (
                    <ClassificationPane
                      title="Function Classification — Low Confidence"
                      paneIcon={<Briefcase className="w-5 h-5 text-emerald-600" />}
                      records={functionItems}
                      classificationKind="function"
                      showFinalClassification
                      showClassificationStatus={false}
                      emptyMessage="No low-confidence function classifications in this date range"
                    />
                  )}
                  {monitoringLinks.function_overridden && (
                    <ClassificationPane
                      title="Function Classification — Overridden"
                      paneIcon={<PenLine className="w-5 h-5 text-violet-600" />}
                      records={overriddenFunctionItems}
                      classificationKind="function"
                      showFinalClassification
                      emptyMessage="No manually overridden function classifications in this date range"
                    />
                  )}
                </div>
              </section>
            )}

            {monitoringLinks?.deleted_records && (
              <section className="space-y-4">
                <DeletedRecordsPane records={deletedItems} />
              </section>
            )}

            {monitoringLinks?.feedback && (
              <section className="space-y-4">
                <FeedbackPane
                  items={feedback.items}
                  statistics={feedback.statistics}
                  exportHref={monitoringLinks.feedback_export?.href}
                  onReviewed={refreshFeedback}
                />
              </section>
            )}
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}
